import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Film, Users, BarChart3, Settings, Plus, Trash2, Edit, Eye,
  TrendingUp, Layers, Upload, X, Save, Search, Play, Tv,
  UserX, UserCheck, KeyRound, ChevronLeft, ChevronRight,
  Tag, Globe, Star, CreditCard, Check, Ticket, Calendar, Percent,
  Volume2, Joystick, ChevronUp, ChevronDown, RefreshCw, Sparkles, SlidersHorizontal, AlertTriangle, Scissors, GripVertical
} from 'lucide-react';
import { TrimEditor, fmtTrim } from './TrimEditor';
import { AiTrailerButton, AiTrailerBulkButton } from './AiTrailer';
import { ConfirmModal } from './TrashTab';
import FilterTimelineEditor from '../FilterTimelineEditor';
import { LanguageStats } from '../analytics/LanguageStats';
import {
  API, CATEGORIES_LIST, ALL_COUNTRIES, MEDIA_SPECS, LANGUAGES, TYPE_LABELS, TYPE_LABELS_PLURAL, uploadFileToS3, FilterBadge,
  formatFileSize, TimecodeInput, CountryPicker, FilterSelect, LoadingSpinner as SharedLoadingSpinner, validateMediaFile
} from './shared';
import { EpisodePreview, EpisodePreviewModal } from './EpisodePreview';
import { LoadingSpinner } from './core';

export function RecommendationsTab({ theme }) {
  const light = theme === 'light';
  const [contents, setContents] = useState([]);
  useEffect(() => { axios.get(`${API}/contents`, { withCredentials: true }).then(r => setContents(r.data)).catch(() => {}); }, []);

  return (
    <div>
      <h1 className={`text-2xl font-bold mb-6 ${light ? 'text-gray-900' : 'text-white'}`}>Recommandations</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className={`rounded-xl p-5 border ${light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5'}`}>
          <Star size={20} className="text-[#E9641A] mb-3" />
          <h3 className={`text-sm font-semibold mb-1 ${light ? 'text-gray-800' : 'text-white'}`}>Basé sur l'historique</h3>
          <p className={`text-[10px] ${light ? 'text-gray-500' : 'text-white/40'}`}>Recommande des contenus similaires à ceux déjà visionnés par l'utilisateur.</p>
          <span className="inline-block mt-3 text-[10px] text-green-400 bg-green-400/10 px-2 py-0.5 rounded">Actif</span>
        </div>
        <div className={`rounded-xl p-5 border ${light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5'}`}>
          <Layers size={20} className="text-blue-400 mb-3" />
          <h3 className={`text-sm font-semibold mb-1 ${light ? 'text-gray-800' : 'text-white'}`}>Basé sur les catégories</h3>
          <p className={`text-[10px] ${light ? 'text-gray-500' : 'text-white/40'}`}>Recommande des contenus de la même catégorie ou genre similaire.</p>
          <span className="inline-block mt-3 text-[10px] text-green-400 bg-green-400/10 px-2 py-0.5 rounded">Actif</span>
        </div>
        <div className={`rounded-xl p-5 border ${light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5'}`}>
          <Film size={20} className="text-purple-400 mb-3" />
          <h3 className={`text-sm font-semibold mb-1 ${light ? 'text-gray-800' : 'text-white'}`}>Contenus similaires</h3>
          <p className={`text-[10px] ${light ? 'text-gray-500' : 'text-white/40'}`}>Association manuelle de contenus similaires par l'admin.</p>
          <span className="inline-block mt-3 text-[10px] text-[#E9641A] bg-[#E9641A]/10 px-2 py-0.5 rounded">Manuel</span>
        </div>
      </div>
      <div className={`rounded-xl p-5 border ${light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5'}`}>
        <h3 className={`text-sm font-semibold mb-3 ${light ? 'text-gray-800' : 'text-white'}`}>Contenus ({contents.length})</h3>
        <div className="space-y-1">
          {contents.slice(0, 15).map(c => (
            <div key={c.id} className={`flex items-center gap-3 p-2 rounded transition-colors ${light ? 'hover:bg-gray-50' : 'hover:bg-white/5'}`}>
              <img src={c.thumbnail_url} alt="" className="w-10 h-14 object-cover rounded flex-none" />
              <div className="flex-1"><p className={`text-xs ${light ? 'text-gray-700' : 'text-white'}`}>{c.title}</p><p className={`text-[10px] capitalize ${light ? 'text-gray-400' : 'text-white/30'}`}>{c.category}</p></div>
              <span className={`text-[10px] ${light ? 'text-gray-400' : 'text-white/25'}`}>{(c.views || 0).toLocaleString()} vues</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── SETTINGS TAB ─── */
export function SettingsTab({ theme }) {
  const light = theme === 'light';
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { axios.get(`${API}/admin/settings`, { withCredentials: true }).then(r => setSettings(r.data)).catch(() => {}); }, []);
  const save = async () => { setSaving(true); await axios.put(`${API}/admin/settings`, settings, { withCredentials: true }); setSaving(false); };
  if (!settings) return <LoadingSpinner />;
  const set = (k, v) => setSettings(prev => ({ ...prev, [k]: v }));
  const inputCls = light ? 'bg-gray-50 border border-gray-200 text-gray-900' : 'bg-white/5 border border-white/8 text-white';

  return (
    <div>
      <h1 className={`text-2xl font-bold mb-6 ${light ? 'text-gray-900' : 'text-white'}`}>Paramètres</h1>
      <div className="space-y-5">
        <div className={`rounded-xl p-5 border ${light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5'}`}>
          <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${light ? 'text-gray-800' : 'text-white'}`}><Settings size={14} className="text-[#E9641A]" /> Branding</h3>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={`block text-[10px] mb-1 ${light ? 'text-gray-500' : 'text-white/35'}`}>Nom</label><input value={settings.name} onChange={e => set('name', e.target.value)} className={`w-full rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`} /></div>
            <div><label className={`block text-[10px] mb-1 ${light ? 'text-gray-500' : 'text-white/35'}`}>Max profils</label><input type="number" value={settings.max_profiles} onChange={e => set('max_profiles', parseInt(e.target.value))} className={`w-full rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`} /></div>
          </div>
        </div>
        <div className={`rounded-xl p-5 border ${light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5'}`}>
          <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${light ? 'text-gray-800' : 'text-white'}`}><Play size={14} className="text-[#E9641A]" /> Player</h3>
          <div className="grid grid-cols-3 gap-3">
            <div><label className={`block text-[10px] mb-1 ${light ? 'text-gray-500' : 'text-white/35'}`}>Qualité max</label><select value={settings.max_quality} onChange={e => set('max_quality', e.target.value)} className={`w-full rounded-lg px-3 py-2 text-xs ${inputCls}`}><option value="4K">4K</option><option value="1080p">1080p</option><option value="720p">720p</option></select></div>
            <label className={`flex items-center gap-2 text-xs pt-5 ${light ? 'text-gray-500' : 'text-white/50'}`}><input type="checkbox" checked={settings.autoplay} onChange={e => set('autoplay', e.target.checked)} className="accent-[#E9641A]" />Autoplay</label>
            <label className={`flex items-center gap-2 text-xs pt-5 ${light ? 'text-gray-500' : 'text-white/50'}`}><input type="checkbox" checked={settings.skip_intro} onChange={e => set('skip_intro', e.target.checked)} className="accent-[#E9641A]" />Skip intro</label>
          </div>
        </div>
        <div className={`rounded-xl p-5 border ${light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5'}`}>
          <h3 className={`text-sm font-semibold mb-3 ${light ? 'text-gray-800' : 'text-white'}`}>Sécurité</h3>
          <label className={`flex items-center gap-2 text-xs ${light ? 'text-gray-500' : 'text-white/50'}`}><input type="checkbox" checked={settings.maintenance_mode || false} onChange={e => set('maintenance_mode', e.target.checked)} className="accent-[#E9641A]" />Mode maintenance</label>
        </div>
        <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-[#E9641A] hover:bg-[#FE975E] text-white font-semibold px-5 py-2 rounded-lg text-sm transition-colors"><Save size={14} /> {saving ? 'Enregistrement...' : 'Enregistrer'}</button>
      </div>
    </div>
  );
}


/* ─── PROMO CODES TAB ─── */

/* ─── FILTER SELECT (imported from shared) ─── */

/* ─── FILTERS TAB ─── */

export function FiltersTab({ theme }) {
  const light = theme === 'light';
  const [filters, setFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ id: '', name: '', action: 'skip', description: '' });

  const fetchFilters = () => {
    axios.get(`${API}/admin/filters`, { withCredentials: true }).then(r => { setFilters(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchFilters(); }, []);

  const sf = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    try {
      if (editingId) {
        await axios.put(`${API}/admin/filters/${editingId}`, form, { withCredentials: true });
      } else {
        if (!form.id || !form.name) { alert('ID et nom requis'); return; }
        await axios.post(`${API}/admin/filters`, form, { withCredentials: true });
      }
      setShowForm(false); setEditingId(null); setForm({ id: '', name: '', action: 'skip', description: '' }); fetchFilters();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
  };

  const startEdit = (f) => { setEditingId(f.id); setForm({ id: f.id, name: f.name, action: f.action, description: f.description || '' }); setShowForm(true); };
  const handleDelete = async (id) => { if (!window.confirm('Supprimer ce filtre ?')) return; await axios.delete(`${API}/admin/filters/${id}`, { withCredentials: true }); fetchFilters(); };

  const inputCls = light ? 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400' : 'bg-white/5 border border-white/8 text-white';
  const cardCls = light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5';

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className={`text-2xl font-bold ${light ? 'text-gray-900' : 'text-white'}`}>Gestion des filtres</h1>
        <button onClick={() => { setEditingId(null); setForm({ id: '', name: '', action: 'skip', description: '' }); setShowForm(!showForm); }} className="flex items-center gap-1.5 bg-[#E9641A] hover:bg-[#FE975E] text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"><Plus size={14} /> Nouveau filtre</button>
      </div>
      <p className={`text-xs mb-4 ${light ? 'text-gray-400' : 'text-white/30'}`}>Créez les types de filtres qui seront disponibles lors de l'ajout de filtres par section sur les films et séries.</p>

      {showForm && (
        <div className={`rounded-xl border p-5 mb-6 ${cardCls}`}>
          <h3 className={`text-sm font-semibold mb-4 ${light ? 'text-gray-800' : 'text-white'}`}>{editingId ? 'Modifier le filtre' : 'Nouveau filtre'}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div><label className={`block text-[10px] mb-1 ${light ? 'text-gray-500' : 'text-white/35'}`}>ID *</label><input value={form.id} onChange={e => sf('id', e.target.value.toLowerCase().replace(/\s/g, '_'))} disabled={!!editingId} placeholder="nudity" className={`w-full rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:border-[#E9641A] ${inputCls} ${editingId ? 'opacity-50' : ''}`} /></div>
            <div><label className={`block text-[10px] mb-1 ${light ? 'text-gray-500' : 'text-white/35'}`}>Nom *</label><input value={form.name} onChange={e => sf('name', e.target.value)} placeholder="Nudité" className={`w-full rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`} /></div>
            <div><label className={`block text-[10px] mb-1 ${light ? 'text-gray-500' : 'text-white/35'}`}>Action</label>
              <select value={form.action} onChange={e => sf('action', e.target.value)} className={`w-full rounded-lg px-3 py-2 text-xs ${inputCls}`}>
                <option value="skip">Sauter la scène</option>
                <option value="mute">Couper le son</option>
              </select>
            </div>
            <div><label className={`block text-[10px] mb-1 ${light ? 'text-gray-500' : 'text-white/35'}`}>Description</label><input value={form.description} onChange={e => sf('description', e.target.value)} placeholder="Optionnel" className={`w-full rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#E9641A] ${inputCls}`} /></div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => { setShowForm(false); setEditingId(null); }} className={`px-4 py-1.5 text-xs border rounded-lg ${light ? 'text-gray-500 border-gray-200' : 'text-white/40 border-white/10'}`}>Annuler</button>
            <button onClick={handleSave} className="px-4 py-1.5 text-xs bg-[#E9641A] hover:bg-[#FE975E] text-white font-semibold rounded-lg flex items-center gap-1.5"><Save size={12} /> {editingId ? 'Modifier' : 'Créer'}</button>
          </div>
        </div>
      )}

      {loading ? <LoadingSpinner /> : (
        <div className="space-y-1.5">
          {filters.length === 0 && <p className={`text-sm text-center py-10 ${light ? 'text-gray-400' : 'text-white/30'}`}>Aucun filtre créé</p>}
          {filters.map(f => (
            <div key={f.id} className={`flex items-center gap-3 p-3 rounded-lg border ${light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5'}`}>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-none ${f.action === 'mute' ? 'bg-purple-500/10' : 'bg-red-500/10'}`}>
                {f.action === 'mute' ? <Volume2 size={16} className="text-purple-400" /> : <Eye size={16} className="text-red-400" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-semibold ${light ? 'text-gray-800' : 'text-white'}`}>{f.name}</p>
                <p className={`text-[10px] ${light ? 'text-gray-400' : 'text-white/30'}`}>
                  <span className="font-mono">{f.id}</span> · {f.action === 'mute' ? 'Couper le son' : 'Sauter la scène'}
                  {f.description ? ` · ${f.description}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-0.5 flex-none">
                <button onClick={() => startEdit(f)} className={`p-1.5 rounded transition-colors ${light ? 'text-gray-300 hover:text-gray-600' : 'text-white/20 hover:text-white'}`}><Edit size={13} /></button>
                <button onClick={() => handleDelete(f.id)} className={`p-1.5 rounded transition-colors ${light ? 'text-gray-300 hover:text-red-500' : 'text-white/20 hover:text-red-400'}`}><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
